import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import { useLocation, useNavigate, useRouteError } from "react-router";
import { toast } from "react-toastify";
import { useDocumentTitle } from "@common/hooks/useDocumentTitle";

export default function RouteErrorFallback() {
    const error = useRouteError();
    const location = useLocation();
    const navigate = useNavigate();

    // Give error screens their own tab title. Without this the tab keeps the would-be
    // page title (e.g. "Settings") or a stale value while a full-page error UI is shown.
    // Covers both child-route errors (TitleManager stays mounted but the matched route's
    // handle is stale) and root-layout errors (TitleManager isn't mounted at all).
    useDocumentTitle("Error");

    useEffect(() => {
        const currentPath = `${location.pathname}${location.search}${location.hash}`;

        Sentry.captureException(error, {
            tags: { boundary: "react-router" },
            extra: { path: currentPath },
        });

        if (location.pathname === "/") {
            toast.error("Something went wrong. Please refresh the app to continue.", {
                toastId: "route-error-stuck",
                autoClose: 8000,
            });
            return;
        }

        toast.error("Something went wrong. We're taking you back home.", {
            toastId: "route-error-recovery",
            autoClose: 6000,
        });
        navigate("/", { replace: true });
    }, [error, location.hash, location.pathname, location.search, navigate]);

    return null;
}
